import { useCallback, useMemo } from "react";
import { useRecoilValue } from "recoil";

import {
  IConflict,
  IRichLogData,
  productionUiDisplayStateSelector,
  productionUiStateSelector,
  useHasPermissions,
} from "@ryde/shared";

interface IUseProductionViewLogsResponse {
  isAutoRedirectEnabled: (conflicts?: IConflict[]) => boolean;
  hasPermissions: boolean;
  isLoading: boolean;
  logData?: IRichLogData;
  isFinished: boolean | undefined;
}

export const useProductionViewLogs = (): IUseProductionViewLogsResponse => {
  const { hasPermissions } = useHasPermissions({
    entity: "rideProduction",
    action: "view_logs",
  });
  const productionUiState = useRecoilValue(productionUiStateSelector);

  const {
    isLoading,
    isFinished,
    totalBookingCount,
    joinedBookingCount,
    producedBookingCount,
  } = useRecoilValue(productionUiDisplayStateSelector);

  const bookingsSkipped = useMemo(
    () =>
      Number(totalBookingCount) -
      Number(joinedBookingCount) -
      Number(producedBookingCount),
    [joinedBookingCount, producedBookingCount, totalBookingCount],
  );

  const isAutoRedirectEnabled = useCallback(
    (conflicts?: IConflict[]) => {
      if (hasPermissions && productionUiState?.logsCount) {
        return false;
      }

      return !bookingsSkipped && !conflicts?.length;
    },
    [bookingsSkipped, hasPermissions, productionUiState?.logsCount],
  );

  return {
    isAutoRedirectEnabled,
    hasPermissions,
    isLoading,
    isFinished,
  };
};
